import {
  Component,
  EventEmitter,
  Output,
  inject,
  signal,
  computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { BusquedaPaginadorComponent } from '../busquedas/busqueda-paginador.component';
import {
  ActividadEconomica,
  ActividadEconomicaFiltro
} from '../../../domain/model/actividad-economica/actividad-economica';
import { ActividadEconomicaRepository } from '../../../domain/service/actividad-economica/actividad-economica-repository';
import {
  sanitizeText,
  sanitizeTextPreserveSpaces
} from 'src/app/common/input-utils';
import {
  DEFAULT_ROWS_PER_PAGE,
  PAGE_SIZE_OPTIONS
} from '../../../common/constants-ejb';
import { buscadorActividadEconomicaPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';

/**
 * Componente reutilizable: Buscador de Actividades Económicas (CIIU)
 *
 * Diálogo modal con filtros y tabla paginada para seleccionar actividades económicas.
 *
 * Uso:
 * ```html
 * <app-buscador-actividad-economica
 *   (actividadSeleccionada)="onActividadSelected($event)">
 * </app-buscador-actividad-economica>
 * ```
 *
 * Se abre con el método `abrir()` del componente.
 *
 * Basado en: UtilidadesBuscadores.buscarActEconomica() del legacy GWT
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Component({
  selector: 'app-buscador-actividad-economica',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    TooltipModule,
    BusquedaPaginadorComponent
  ],
  styleUrls: ['../styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      [(visible)]="visible"
      header="Buscar Actividad Económica"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '48rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      [closable]="true"
      [draggable]="false"
      appendTo="body"
    >
      <!-- Filtros de Búsqueda -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Código Actividad Económica</label
          >
          <input
            pInputText
            [(ngModel)]="filtro.codigo"
            (input)="sanitizarCampo('codigo')"
            placeholder="Código CIIU..."
            maxlength="10"
            class="w-full"
            (keyup.enter)="buscar()"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Nombre Actividad Económica</label
          >
          <input
            pInputText
            [(ngModel)]="filtro.nombre"
            (input)="sanitizarCampo('nombre')"
            placeholder="Descripción..."
            maxlength="200"
            class="w-full"
            (keyup.enter)="buscar()"
          />
        </div>
      </div>

      <!-- Botones de acción -->
      <div
        class="flex flex-wrap gap-2 sm:gap-3 mb-4 justify-end sm:justify-start"
      >
        <button
          pButton
          label="Limpiar"
          icon="pi pi-eraser"
          class="p-button-secondary p-button-outlined"
          (click)="limpiar()"
        ></button>
        <button
          pButton
          label="Buscar"
          icon="pi pi-search"
          class="p-button-primary"
          [loading]="loading()"
          (click)="buscar()"
        ></button>
      </div>

      <!-- Resultados -->
      @if (busquedaRealizada()) {
        <div
          class="font-medium text-gray-700 dark:text-gray-200 p-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm flex justify-between items-center"
        >
          <span>Listado de Actividades Económicas</span>
          @if (totalElements() > 0) {
            <span class="text-xs text-gray-500 dark:text-gray-400">
              Registros del {{ registroInicio() }} hasta el {{ registroFin() }} de
              {{ totalElements() | number }}
            </span>
          }
        </div>

        <div class="md:hidden space-y-3 p-3">
          @for (act of actividades(); track act.cdciiu) {
            <div
              class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
            >
              <div class="mb-3 min-w-0">
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  Nombre Actividad Económica
                </div>
                <div
                  class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                  [attr.title]="tituloTabla(act.dsciiu)"
                >
                  {{ textoTabla(act.dsciiu) }}
                </div>
              </div>
              <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Código:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(act.cdciiu)
                  }}</span>
                </div>
              </div>
              <p-button
                label="Seleccionar"
                icon="pi pi-check"
                size="small"
                styleClass="w-full min-h-[44px]"
                (onClick)="seleccionar(act)"
              />
            </div>
          }
        </div>

        <div class="hidden md:block">
          <div
            role="region"
            [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
            class="sr-only"
          ></div>
          <p-table
            [resizableColumns]="true"
            columnResizeMode="expand"
            [value]="actividades()"
            [loading]="loading()"
            responsiveLayout="scroll"
            [styleClass]="tableStyleClass()"
            [scrollable]="true"
            [tableStyle]="{
              'min-width': tableConfig().responsive.minWidth || 'max-content'
            }"
            [scrollHeight]="tableConfig().responsive.scrollHeight || '300px'"
          >
            <ng-template pTemplate="header">
              <tr>
                <th pResizableColumn>
                  <span
                    class="grid-header-text"
                    title="Código Actividad Económica"
                    >Código Actividad Económica</span
                  >
                </th>
                <th pResizableColumn>
                  <span
                    class="grid-header-text"
                    title="Nombre Actividad Económica"
                    >Nombre Actividad Económica</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Seleccionar"
                    >Seleccionar</span
                  >
                </th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-act>
              <tr>
                <td class="font-mono">
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(act.cdciiu)"
                    >{{ textoTabla(act.cdciiu) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(act.dsciiu)"
                    >{{ textoTabla(act.dsciiu) }}</span
                  >
                </td>
                <td class="text-center">
                  <button
                    pButton
                    label="Seleccionar"
                    class="p-button-sm p-button-text"
                    (click)="seleccionar(act)"
                  ></button>
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="3" class="text-center p-4 text-gray-500">
                  No se encontraron actividades económicas
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <!-- Paginación -->
        <app-busqueda-paginador
          [currentPage]="currentPage()"
          [totalPages]="totalPages()"
          [hasPrevious]="hasPrevious()"
          [hasNext]="hasNext()"
          [pageSizeOptions]="pageSizeOptions"
          [selectedPageSize]="pageSize()"
          (pageSizeChange)="onPageSizeChange($event)"
          (first)="irPrimeraPagina()"
          (previous)="paginaAnterior()"
          (next)="paginaSiguiente()"
          (last)="irUltimaPagina()"
        ></app-busqueda-paginador>
      }
    </p-dialog>
  `
})
export class BuscadorActividadEconomicaComponent {
  @Output() actividadSeleccionada = new EventEmitter<ActividadEconomica>();

  // Inyectando PUERTO (no adaptador) - Arquitectura Hexagonal
  private readonly actividadService = inject(ActividadEconomicaRepository);

  visible = false;
  filtro: ActividadEconomicaFiltro = {};

  // Estado
  actividades = signal<ActividadEconomica[]>([]);
  loading = signal(false);
  busquedaRealizada = signal(false);

  // Paginación por cursor (keyset, ver skill acceso-datos-oracle §7)
  currentPage = signal(1);
  pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  totalElements = signal(0);

  /** totalPages es solo aproximado (para mostrar "Página X de Y") — con
   * keyset no hay conteo exacto de páginas navegables por cursor. */
  readonly totalPages = computed(() => {
    const size = this.pageSize();
    const total = this.totalElements();
    return size > 0 && total > 0 ? Math.ceil(total / size) : 0;
  });

  // Los botones Anterior/Siguiente dependen de la respuesta del servidor
  // (skill frontend-girop §17.7), no de currentPage/totalPages.
  hasPrevious = signal(false);
  hasNext = signal(false);

  private nextCursorActual = signal<string | null>(null);
  private previousCursorActual = signal<string | null>(null);

  readonly tableConfig = computed(() =>
    buscadorActividadEconomicaPreset.createConfig({
      data: this.actividades() as unknown as Record<string, unknown>[],
      style: {
        tableClassName: 'tabla-buscador-actividad-economica',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.loading(),
        emptyMessage: 'No se encontraron actividades economicas'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: 'max-content',
        scrollHeight: '300px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de busqueda de actividades economicas',
        announceSelection: false,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tableStyleClass = computed(() => {
    const config = this.tableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  registroInicio = () => {
    if (this.totalElements() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  };

  registroFin = () => {
    if (this.totalElements() === 0) return 0;
    return Math.min(this.currentPage() * this.pageSize(), this.totalElements());
  };

  /**
   * Abre el diálogo de búsqueda
   */
  abrir(): void {
    this.visible = true;
    this.limpiar();
  }

  /**
   * Cierra el diálogo
   */
  cerrar(): void {
    this.visible = false;
  }

  /**
   * Ejecuta la búsqueda
   */
  async buscar(): Promise<void> {
    this.sanitizarCampo('codigo', true);
    this.sanitizarCampo('nombre', true);
    // Búsqueda nueva: siempre página 1
    await this.cargarDatos(null, 'NEXT', 1);
  }

  /**
   * Sanitización XSS de texto libre (seguridad-girop §3). Mientras se teclea
   * conserva los espacios; antes de buscar también los recorta.
   */
  sanitizarCampo(campo: 'codigo' | 'nombre', recortar = false): void {
    const valor = this.filtro[campo];
    if (valor === undefined) return;
    this.filtro[campo] = recortar
      ? sanitizeText(valor)
      : sanitizeTextPreserveSpaces(valor);
  }

  /**
   * Limpia filtros y resultados
   */
  limpiar(): void {
    this.filtro = {};
    this.actividades.set([]);
    this.busquedaRealizada.set(false);
    this.totalElements.set(0);
    this.nextCursorActual.set(null);
    this.previousCursorActual.set(null);
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.currentPage.set(1);
  }

  /**
   * Selecciona una actividad económica y cierra el diálogo
   */
  seleccionar(actividad: ActividadEconomica): void {
    this.actividadSeleccionada.emit(actividad);
    this.cerrar();
  }

  // Navegación por cursor
  irPrimeraPagina(): void {
    if (this.loading()) {
      return;
    }
    this.cargarDatos(null, 'NEXT', 1);
  }

  paginaAnterior(): void {
    if (this.loading() || !this.hasPrevious()) {
      return;
    }
    this.cargarDatos(
      this.previousCursorActual(),
      'PREVIOUS',
      Math.max(1, this.currentPage() - 1)
    );
  }

  paginaSiguiente(): void {
    if (this.loading() || !this.hasNext()) {
      return;
    }
    this.cargarDatos(this.nextCursorActual(), 'NEXT', this.currentPage() + 1);
  }

  irUltimaPagina(): void {
    if (this.loading() || this.totalElements() === 0) {
      return;
    }
    this.cargarDatos(null, 'LAST', this.totalPages());
  }

  onPageSizeChange(newPageSize: number): void {
    const pageSize = Number(newPageSize);
    if (!Number.isFinite(pageSize) || pageSize <= 0 || pageSize === this.pageSize()) {
      return;
    }

    this.pageSize.set(pageSize);
    this.currentPage.set(1);

    if (this.busquedaRealizada()) {
      this.cargarDatos(null, 'NEXT', 1);
    }
  }

  private async cargarDatos(
    cursor: string | null,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST',
    pagina = 1
  ): Promise<void> {
    this.loading.set(true);
    try {
      const response = await this.actividadService.search(
        this.filtro, cursor, this.pageSize(), direction
      );

      this.actividades.set(response.content);
      this.totalElements.set(response.totalElements);
      this.nextCursorActual.set(response.nextCursor ?? null);
      this.previousCursorActual.set(response.previousCursor ?? null);
      this.hasNext.set(response.hasNext);
      this.hasPrevious.set(response.hasPrevious);
      // La página solo cambia tras una respuesta exitosa. Con LAST se calcula
      // a partir del total que devuelve el servidor.
      this.currentPage.set(
        direction === 'LAST'
          ? Math.max(1, Math.ceil(response.totalElements / this.pageSize()))
          : pagina
      );
      this.busquedaRealizada.set(true);
    } catch (error) {
      console.error('Error al buscar actividades económicas:', error);
      this.actividades.set([]);
    } finally {
      this.loading.set(false);
    }
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }
}
